import { defaultColumns, defaultRows, parseLayout, screenMeta } from '@racedash/shared'
import type { DashboardLayout, ScreenEntry } from '@racedash/shared'

/**
 * What a dashboard can be started from.
 *
 * Chosen when the dashboard is created and written into the document, rather
 * than filled in when nothing is stored. The fallback was the original
 * arrangement and it was quietly incoherent: the dashboard *looked* populated
 * while its document was empty, so Save was disabled and there was no way to
 * keep what you were looking at.
 */
export type TemplateId = 'standard' | 'timing' | 'empty'

export const templates: { id: TemplateId; label: string; description: string }[] = [
  {
    id: 'standard',
    label: 'Everything',
    description: 'A widget grid, live timing and stints — one tab each.',
  },
  {
    id: 'timing',
    label: 'Live timing only',
    description: 'The field as a table, with race control above it.',
  },
  {
    id: 'empty',
    label: 'Empty',
    description: 'No screens. Add what you want.',
  },
]

/**
 * The grid a new dashboard's widget screen starts from.
 *
 * Run through parseLayout rather than asserted, so a template is held to the
 * same reading as any stored layout — a typo here fails the way a bad document
 * would, instead of being the one layout in the system that skips validation.
 */
export const standardLayout: DashboardLayout = parseLayout({
  columns: defaultColumns,
  rows: defaultRows,
  widgets: [
    // The top band answers "where are we", left to right in the order someone
    // asks it, with the countdown given the width because it is the one thing
    // in the garage everybody looks at.
    { id: 'car', x: 0, y: 0, w: 2, h: 2, config: { kind: 'carNumber' } },
    { id: 'pos', x: 2, y: 0, w: 2, h: 2, config: { kind: 'position', of: 'overall' } },
    { id: 'cls', x: 4, y: 0, w: 2, h: 2, config: { kind: 'position', of: 'class' } },
    { id: 'lap', x: 6, y: 0, w: 2, h: 2, config: { kind: 'lapNumber' } },
    { id: 'remaining', x: 8, y: 0, w: 4, h: 2, config: { kind: 'clock', show: 'remaining' } },

    // The middle band is pace: what we just did, what we can do, and how far the
    // lap in progress has got.
    { id: 'last', x: 0, y: 2, w: 3, h: 2, config: { kind: 'lapTime', which: 'last' } },
    { id: 'best', x: 3, y: 2, w: 3, h: 2, config: { kind: 'lapTime', which: 'best' } },
    { id: 'sectors', x: 6, y: 2, w: 6, h: 2, config: { kind: 'sectors' } },

    // The bottom half is what you read rather than glance at, plus the two that
    // have to be visible without being read: track state, and the time.
    {
      id: 'laps',
      x: 0,
      y: 4,
      w: 6,
      h: 4,
      config: { kind: 'lapList', rows: 10, columns: ['lap', 'time', 's1', 's2', 's3'] },
    },
    { id: 'rc', x: 6, y: 4, w: 4, h: 4, config: { kind: 'messages', rows: 8 } },
    { id: 'track', x: 10, y: 4, w: 2, h: 2, config: { kind: 'trackStatus' } },
    { id: 'tod', x: 10, y: 6, w: 2, h: 2, config: { kind: 'clock', show: 'timeOfDay' } },
  ],
})

export const emptyLayout: DashboardLayout = {
  columns: defaultColumns,
  rows: defaultRows,
  widgets: [],
}

/** The screens a new dashboard starts with. */
export function screensForTemplate(id: TemplateId): ScreenEntry[] {
  const timing: ScreenEntry = {
    id: 'timing-1',
    name: screenMeta.timing.defaultName,
    config: { ...screenMeta.timing.default },
  }

  switch (id) {
    case 'empty':
      return []
    case 'timing':
      return [timing]
    case 'standard':
      return [
        {
          id: 'grid-1',
          name: screenMeta.grid.defaultName,
          config: { kind: 'grid', layout: standardLayout },
        },
        timing,
        { id: 'stints-1', name: screenMeta.stints.defaultName, config: { kind: 'stints' } },
      ]
  }
}
